import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useServices } from "../context/ServicesContext";
import { getImageUrl } from "../utils/utils";
import ICON_MAP from "./serviceIcons";

const ServicesSection = ({ data }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { services } = useServices();

  const isLoading = data === undefined;

  return (
    <Box
      id="services"
      sx={{
        py: 10,
        backgroundColor: theme.palette.background.service,
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Chip
            label={t("pages.home.services.chip_title")}
            sx={{
              fontFamily: "Syne, sans-serif",
              backgroundColor: theme.palette.background.chip,
              color: theme.palette.custom.chipText,
              fontWeight: 600,
              fontSize: "0.875rem",
              lineHeight: "1.25rem",
              mb: 2,
              p: 1,
            }}
          />
          <Typography
            variant="h3"
            component="h2"
            fontWeight="bold"
            sx={{
              background: theme.palette.custom.headingGradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
              width: { xs: "100%", sm: "80%", md: "60%" },
              margin: "0 auto",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem", lg: "3rem" },
              lineHeight: { xs: "2rem", sm: "2.25rem", md: "2.5rem", lg: "1" },
              display: "flex",
              justifyContent: "center",
            }}
          >
            {isLoading ? (
              <Skeleton variant="text" height={60} width="80%" />
            ) : (
              data?.meta?.title || t("pages.home.services.headline")
            )}
          </Typography>
          <Typography
            variant="p"
            component="p"
            color={theme.palette.text.secondary}
            sx={{
              mt: 2,
              mb: 6,
              px: { xs: 2, md: 0 },
              fontSize: "1.125rem",
              lineHeight: "1.75rem",
            }}
          >
            {isLoading ? (
              <Skeleton variant="text" height={30} width="100%" />
            ) : (
              data?.meta?.description || t("pages.home.services.subheadline")
            )}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 4,
          }}
        >
          {services && services.length > 0 ? (
            services.map((service) => (
              <Card
                key={service._id}
                sx={{
                  height: "100%",
                  borderRadius: "16px",
                  boxShadow: theme.palette.custom.cardShadow,
                  transition: "all 0.3s ease-in-out",
                  overflow: "hidden",
                  border: "1px solid transparent",
                  "&:hover": {
                    boxShadow: theme.palette.custom.cardShadowHover,
                    borderColor: theme.palette.primary.main,
                    "& .hover-overlay": {
                      opacity: 1,
                    },
                    "& .service-icon": {
                      transform: "scale(1.15)",
                    },
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 4,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Box
                    className="service-icon"
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "12px",
                      background: theme.palette.custom.serviceIconBg,
                      color: theme.palette.custom.serviceIconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      transition: "transform 0.3s ease-in-out",
                    }}
                  >
                    {(() => {
                      const IconComponent =
                        ICON_MAP[service.icon] || ICON_MAP["default"]; // use a known fallback key
                      return IconComponent ? (
                        <IconComponent
                          sx={{ fontSize: 28, color: "inherit" }}
                        />
                      ) : null;
                    })()}
                  </Box>

                  <Typography
                    variant="h5"
                    component="h3"
                    fontWeight="600"
                    sx={{
                      mb: 2,
                      fontFamily: theme.typography.h3.fontFamily,
                      typography: "textXl",
                    }}
                  >
                    {service.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {service.description}
                  </Typography>

                  <List disablePadding sx={{ mb: 2 }}>
                    {service.features.slice(0, 3).map((point, index) => (
                      <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 16 }}>
                          <FiberManualRecordIcon
                            sx={{
                              fontSize: 8,
                              color: theme.palette.primary.light,
                            }}
                          />
                        </ListItemIcon>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="500"
                          typography="textSm"
                        >
                          {point}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    onClick={() => window.open(service?.linkUrl, "_blank")}
                    endIcon={<ArrowForwardIcon />}
                    disabled={!service?.linkUrl}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: "500",
                      px: 0,
                      color: theme.palette.primary.main,
                      typography: "textSm",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {t("pages.home.services.get_started")}
                  </Button>
                </CardContent>
              </Card>
            ))
          ) : (
            <Box sx={{ mt: 4, gridColumn: "1 / -1" }}>
              <Typography
                variant="h6"
                color="text.secondary"
                textAlign="center"
              >
                {t("pages.home.services.no_services")}
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesSection;
